/**
 * Drag and Drop Utility
 */

export default function createDragDropHandler (config) {
  const { $scope, $element, $log, reorderItems, setPlaceholderIndex } = config

  // Simple state
  const state = {
    isDragging: false,
    draggedId: null,
    holdTimer: null,
    lastDebugTime: null,
    lastScrollDebug: null,
    // Scroll state for game loop
    scrollDirection: 0, // -1 (up), 0 (stop), 1 (down)
    scrollSpeed: 0,
    clientY: 0,
    scrollInterval: null
  }

  // Throttle $scope.$apply to avoid performance issues on mobile
  let applyTimer = null
  function throttledApply () {
    if (applyTimer) return // Already scheduled
    applyTimer = setTimeout(() => {
      $scope.$apply()
      applyTimer = null
    }, 100) // Apply every 100ms
  }

  // Cache mobile detection result to avoid repeated calls
  let mobileDeviceCache = null

  // Simple device detection for scroll optimization
  function isMobileDevice () {
    // Return cached result if already determined
    if (mobileDeviceCache !== null) {
      return mobileDeviceCache
    }

    const userAgent = navigator.userAgent
    // Stricter check: Only consider it mobile if it's Android/iOS.
    // This avoids flagging desktops with touch screens as mobile.
    const isAndroid = /Android/i.test(userAgent)
    const isIOS = /iPhone|iPad|iPod/i.test(userAgent)

    // Cache the result
    mobileDeviceCache = isAndroid || isIOS

    // Debug mobile detection - ONLY ONCE
    console.log('🤖 [MobileDetection] CACHED RESULT:', {
      userAgent,
      isAndroid,
      isIOS,
      finalResult: mobileDeviceCache
    })

    return mobileDeviceCache
  }

  function autoScroll (clientY) {
    const scrollContainer = $element[0].querySelector('.scene-content-scrollable')
    if (!scrollContainer) {
      state.scrollDirection = 0
      state.scrollSpeed = 0
      return
    }

    const containerRect = scrollContainer.getBoundingClientRect()

    // Debug: Log container info occasionally
    if (state.isDragging && (!state.lastDebugTime || (Date.now() - state.lastDebugTime > 1000))) {
      state.lastDebugTime = Date.now()
      console.log('📦 [AutoScroll] Container:', {
        top: containerRect.top,
        bottom: containerRect.bottom,
        height: containerRect.height,
        clientY,
        scrollTop: scrollContainer.scrollTop,
        scrollHeight: scrollContainer.scrollHeight,
        clientHeight: scrollContainer.clientHeight
      })
    }

    // --- Graduated Speed Logic ---
    const scrollZonePercent = isMobileDevice() ? 0.20 : 0.15 // 20% mobile, 15% desktop
    const SCROLL_ZONE = containerRect.height * scrollZonePercent

    // Speed is in pixels per frame. Increased significantly for mobile.
    const MIN_SPEED = isMobileDevice() ? 5 : 2
    const MAX_SPEED = isMobileDevice() ? 60 : 25

    const distanceFromTop = clientY - containerRect.top
    const distanceFromBottom = containerRect.bottom - clientY
    const canScrollUp = scrollContainer.scrollTop > 0
    const canScrollDown = scrollContainer.scrollTop < (scrollContainer.scrollHeight - scrollContainer.clientHeight)

    // Default to no scroll
    state.scrollDirection = 0
    state.scrollSpeed = 0
    let zoneName = 'none'
    let percentIntoZone = 0

    // Determine scroll direction and speed
    if (distanceFromTop < SCROLL_ZONE && canScrollUp) {
      state.scrollDirection = -1
      percentIntoZone = (SCROLL_ZONE - distanceFromTop) / SCROLL_ZONE
      zoneName = 'top-zone'
    } else if (distanceFromBottom < SCROLL_ZONE && canScrollDown) {
      state.scrollDirection = 1
      percentIntoZone = (SCROLL_ZONE - distanceFromBottom) / SCROLL_ZONE
      zoneName = 'bottom-zone'
    }

    // Handle scrolling when outside the container
    if (distanceFromTop < 0 && canScrollUp) {
      state.scrollDirection = -1
      percentIntoZone = 1.0 // Max speed
      zoneName = 'above-container'
    } else if (distanceFromBottom < 0 && canScrollDown) {
      state.scrollDirection = 1
      percentIntoZone = 1.0 // Max speed
      zoneName = 'below-container'
    }

    // Calculate speed if we are in a zone
    if (state.scrollDirection !== 0) {
      // Ensure percent is between 0 and 1
      const clampedPercent = Math.max(0, Math.min(1, percentIntoZone))
      // Simple linear interpolation for speed
      state.scrollSpeed = MIN_SPEED + (clampedPercent * (MAX_SPEED - MIN_SPEED))
    }

    // Debug: Log scroll decision occasionally
    if (state.isDragging && (!state.lastScrollDebug || (Date.now() - state.lastScrollDebug > 250))) {
      state.lastScrollDebug = Date.now()
      console.log('⚡ [AutoScroll]', {
        zone: zoneName,
        direction: state.scrollDirection === -1 ? 'UP' : state.scrollDirection === 1 ? 'DOWN' : 'NONE',
        speed: state.scrollSpeed,
        percentIntoZone: percentIntoZone.toFixed(2),
        distanceFromTop: Math.round(distanceFromTop),
        distanceFromBottom: Math.round(distanceFromBottom),
        isMobile: isMobileDevice()
      })
    }
  }

  // Game loop for smooth scrolling using setInterval
  function scrollLoop () {
    // Always calculate scroll speed based on the latest clientY
    autoScroll(state.clientY)

    // Read state and perform scroll
    if (state.scrollDirection !== 0) {
      const scrollContainer = $element[0].querySelector('.scene-content-scrollable')
      if (scrollContainer) {
        const newScrollTop = scrollContainer.scrollTop + (state.scrollDirection * state.scrollSpeed)
        scrollContainer.scrollTop = newScrollTop
      }
    }
  }

  function startScrollLoop () {
    if (state.scrollInterval) {
      clearInterval(state.scrollInterval)
    }
    // Use setInterval instead of requestAnimationFrame for consistent timing
    // 16ms = ~60 FPS, which should work better on Android
    state.scrollInterval = setInterval(scrollLoop, 16)
  }

  function stopScrollLoop () {
    if (state.scrollInterval) {
      clearInterval(state.scrollInterval)
      state.scrollInterval = null
    }
    state.scrollDirection = 0 // Clean up
  }

  // Desktop handlers
  function handleDragStart (event) {
    console.log('🖱️ [DragDrop] Drag Start Triggered')
    const target = event.target.closest('[data-position]')
    if (target) {
      event.dataTransfer.setData('text/plain', target.getAttribute('data-index'))
      event.dataTransfer.effectAllowed = 'move'

      // Set initial finger/mouse position
      state.clientY = event.clientY

      // Start scroll loop
      state.isDragging = true
      startScrollLoop()
    }
  }

  function handleDragOver (event) {
    event.preventDefault()
    // Store the current clientY. The scrollLoop will handle the rest.
    state.clientY = event.clientY

    const target = event.target.closest('[data-position]')
    if (target) {
      setPlaceholderIndex(parseInt(target.getAttribute('data-position')))
      throttledApply() // Use throttled apply for performance
    }
  }

  function handleDrop (event) {
    event.preventDefault()
    const sourceId = parseInt(event.dataTransfer.getData('text/plain'))
    const target = event.target.closest('[data-index]')

    if (target && sourceId) {
      const targetId = parseInt(target.getAttribute('data-index'))
      if (targetId && targetId !== sourceId) {
        reorderItems(sourceId, targetId)
      }
    }

    setPlaceholderIndex(null)
    state.isDragging = false
    stopScrollLoop()
    // Final apply to ensure UI is up to date
    $scope.$apply()
  }

  function handleDragEnd () {
    setPlaceholderIndex(null)
    state.isDragging = false
    stopScrollLoop()
    $scope.$apply()
  }

  // Public API
  return {
    initialize () {
      $log.debug('🎯 [DragDrop] Initializing listeners')
      $element[0].addEventListener('dragstart', handleDragStart)
      $element[0].addEventListener('dragover', handleDragOver)
      $element[0].addEventListener('drop', handleDrop)
      $element[0].addEventListener('dragend', handleDragEnd)
    },

    destroy () {
      $log.debug('🎯 [DragDrop] Destroying listeners')
      stopScrollLoop() // Clean up any running intervals
      $element[0].removeEventListener('dragstart', handleDragStart)
      $element[0].removeEventListener('dragover', handleDragOver)
      $element[0].removeEventListener('drop', handleDrop)
      $element[0].removeEventListener('dragend', handleDragEnd)
    }
  }
}
